/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * ST-6 (FCR S733) -- a page part that failed to download is recovered by one
 * reload, not left broken until the customer thinks of reloading.
 *
 * Each page of the shop is downloaded the first time it is opened. When the
 * connection drops at that moment the download fails, and it stays failed:
 * nothing asks for it again. In the desktop purchase window, which has no
 * address bar and no reload button, that is a dead end.
 *
 * Two places notice it, and both call {@link recoverFromChunkFailure}:
 *
 *  - Vite's own `vite:preloadError` event, which fires when a page part fails
 *    to download (Vite's documented remedy is exactly this: reload);
 *  - the ErrorBoundary, for the same failure surfacing as a render error.
 *
 * The guard is a time window, not a once-per-tab flag. The old flag
 * (`chunk_reloaded`) was set on the first recovery and never cleared, so the
 * second dropped connection in the same tab -- the ordinary case in a desktop
 * window that stays open all day -- was never recovered. A window of one
 * minute still stops a loop: a reload that fails the same way within the
 * minute is not reloaded again, and the error is shown.
 *
 * When the browser knows it is offline, the reload waits for the connection:
 * reloading into no network would only swap one broken page for another.
 */

export const CHUNK_RELOAD_KEY = "deskshare:chunk-reload-at";

/** A second failure within this long of the last recovery is not reloaded. */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

interface RecoveryEnvironment {
  now: () => number;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  isOnline: () => boolean;
  reload: () => void;
  onceOnline: (callback: () => void) => void;
}

function browserEnvironment(): RecoveryEnvironment {
  let storage: RecoveryEnvironment["storage"] = null;
  try {
    storage = window.sessionStorage;
  } catch {
    // Blocked storage (a private window, a locked-down desktop host): recover
    // anyway, without the loop guard -- see recoverFromChunkFailure.
    storage = null;
  }
  return {
    now: () => Date.now(),
    storage,
    isOnline: () => (typeof navigator === "undefined" ? true : navigator.onLine !== false),
    reload: () => window.location.reload(),
    onceOnline: (callback) =>
      window.addEventListener("online", callback, { once: true }),
  };
}

/**
 * Recover from a failed page-part download by reloading once.
 *
 * @returns true when a reload was performed or scheduled; false when the
 * guard refused it (a recovery already happened within the window), in which
 * case the caller shows the error.
 */
export function recoverFromChunkFailure(
  env: RecoveryEnvironment = browserEnvironment(),
): boolean {
  const now = env.now();
  let last = 0;
  try {
    last = Number(env.storage?.getItem(CHUNK_RELOAD_KEY) ?? 0) || 0;
  } catch {
    last = 0;
  }
  if (last && now - last < CHUNK_RELOAD_WINDOW_MS) return false;

  try {
    env.storage?.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // Without storage there is no loop guard, but a single failed download is
    // still better recovered than not; the window check above simply cannot
    // run on the next load.
  }

  if (env.isOnline()) {
    env.reload();
  } else {
    env.onceOnline(() => env.reload());
  }
  return true;
}

/** True for the errors a failed page-part download produces. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  return (
    name === "ChunkLoadError" ||
    (typeof message === "string" &&
      /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
        message,
      ))
  );
}

/**
 * Listen for Vite's preload failures. Called once, from main.tsx.
 * `preventDefault()` stops Vite rethrowing the error, since the page is about
 * to reload; when the guard refuses, the error is left to surface.
 */
export function installChunkFailureRecovery(target: Window = window): void {
  target.addEventListener("vite:preloadError", (event: Event) => {
    if (recoverFromChunkFailure()) event.preventDefault();
  });
}
